import JsBarcode from 'jsbarcode';
import type { PrintJob } from '@/models/FulfillmentPrint';
import { escapeHtml } from '@/utils/escapeHtml';
import { LABEL_PAGE } from './labelSheet';

/** One bar on a 203dpi thermal head is 0.125mm; three of them stay readable after the ribbon smears. */
const MODULE_MM = 0.375;

const BARCODE_HEIGHT_MM = 24;

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface ProductCodeLabelInput {
  code: string;
  sku: string;
  /** Brand and name as the desk reads them; the SKU stands in without it. */
  name?: string | undefined;
  /** The Korean brand and name, printed under the English one so the desk can match the product by its Korean pack. */
  nameKr?: string | undefined;
}

/** The code as EAN-13 bars, in millimetres so every printer draws it the same size. The digits print as text below. */
const barsOf = (code: string): string => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  // One unit per module with an eleven-module quiet zone either side, as EAN-13 asks; none above or below, so the
  // bars take the whole height.
  JsBarcode(svg, code, {
    format: 'EAN13',
    width: 1,
    height: 60,
    marginLeft: 11,
    marginRight: 11,
    marginTop: 0,
    marginBottom: 0,
    displayValue: false,
    flat: true,
  });
  const modules = parseFloat(svg.getAttribute('width') ?? '');
  const height = parseFloat(svg.getAttribute('height') ?? '');
  if (!(modules > 0 && height > 0)) {
    throw new Error(`The barcode ${code} could not be drawn.`);
  }
  svg.setAttribute('viewBox', `0 0 ${modules} ${height}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  // Hard bar edges: an anti-aliased grey edge is what a thermal driver dithers into a blurred bar.
  svg.setAttribute('shape-rendering', 'crispEdges');
  svg.setAttribute('width', `${modules * MODULE_MM}mm`);
  svg.setAttribute('height', `${BARCODE_HEIGHT_MM}mm`);
  svg.removeAttribute('style');
  return svg.outerHTML;
};

/**
 * The sticker for a product given an internal code (SSD-7611): the name and its Korean name, the bars, the digits and the SKU, on
 * the same 100x150mm label stock the desk already prints its sheets on. Stuck on the product, it is what the
 * next scan reads.
 */
export const productCodeLabelHtml = ({ code, sku, name, nameKr }: ProductCodeLabelInput): string => {
  const { widthMm, heightMm } = LABEL_PAGE;
  const title = name?.trim() || sku;
  const titleKr = nameKr?.trim();
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(`Product code ${code}`)}</title>
    <style>
      @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif; color: #000; background: #fff; }
      .label { width: ${widthMm}mm; height: ${heightMm}mm; padding: 6mm 8mm; display: flex; flex-direction: column;
        align-items: center; justify-content: center; gap: 3mm; overflow: hidden; }
      .name, .name-kr { font-size: 5mm; font-weight: 700; line-height: 1.25; text-align: center; max-height: 12.5mm;
        overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
      .digits { font-size: 6mm; font-weight: 700; letter-spacing: 1.2mm; font-variant-numeric: tabular-nums; }
      .sku { font-size: 4mm; }
    </style></head><body>
    <section class="label">
      <div class="name">${escapeHtml(title)}</div>
      ${titleKr ? `<div class="name-kr">${escapeHtml(titleKr)}</div>` : ''}
      ${barsOf(code)}
      <div class="digits">${escapeHtml(code)}</div>
      <div class="sku">SKU ${escapeHtml(sku)}</div>
    </section>
  </body></html>`;
};

/** The sticker as a print job for the label printer. */
export const productCodeLabelJob = (input: ProductCodeLabelInput): PrintJob => ({
  surface: 'productCodeLabel',
  jobKey: `product-code-label-${input.code}`,
  role: 'label',
  format: 'html',
  html: productCodeLabelHtml(input),
});
